import { useGetSettingsSiteSeparationCloudTags } from '@ipfabric/api-client/client/model/site-separation/site-separation';
import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';

export type CloudResourceTagOptions = {
  options: { label: string; value: string }[];
  /** Non-null when the fetch failed; render it so an empty list isn't ambiguous. */
  errorMessage: string | null;
  /**
   * True while the tag names are in flight. AutocompleteInput blocks ArrowDown on
   * this, so a caller also using `hideMenuWhenEmpty` doesn't look dead mid-fetch.
   */
  loading: boolean;
};

/**
 * Loads tag names for the `cloudResourceTag` picker, shared by the rule editor
 * and the Test rule panel. A null `snapshotId` (Global settings) returns names
 * across all snapshots. `enabled` lets callers mount the hook unconditionally.
 */
export const useCloudResourceTagOptions = (
  snapshotId: string | null,
  enabled = true,
): CloudResourceTagOptions => {
  const query = useGetSettingsSiteSeparationCloudTags(
    snapshotId ? { snapshot: snapshotId } : undefined,
    { query: { enabled } },
  );

  const apiMessage =
    (query.error &&
      isIpfError(query.error, 'API') &&
      query.error.payload?.response.message) ||
    null;

  return {
    options: query.error
      ? []
      : (query.data?.tagNames ?? []).map((tag) => ({
          label: tag,
          value: tag,
        })),
    errorMessage: query.error
      ? apiMessage
        ? `Failed to load tags: ${apiMessage}`
        : 'Failed to load tags. Please try again.'
      : null,
    loading: query.isFetching,
  };
};
